import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  Package,
  LogOut,
  Target,
  FileBarChart,
  Bot,
  ChevronDown,
  Menu,
  KeyRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ChangePasswordModal } from "@/components/users/ChangePasswordModal";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/organizations", label: "Customers", icon: Users }, // Route updated, label kept for BC
  { to: "/services", label: "Services & Plans", icon: Package },
  { to: "/leads", label: "CRM", icon: Target },
  { to: "/users", label: "Users", icon: UserCog },
];

const menuEditorItems = [
  { to: "/menu-editor", label: "Overview", icon: LayoutDashboard },
  { to: "/menu-editor/imports", label: "AI Import", icon: FileBarChart },
  { to: "/menu-editor/ai-asistent", label: "AI Asistent", icon: Bot },
];

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const isMenuEditorActive = location.pathname.startsWith("/menu-editor");

  return (
    <>
      <div className="flex h-16 items-center border-b px-6">
        <span className="text-xl font-bold">ETAPA ERP</span>
      </div>
      <nav className="space-y-1 p-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}

        {/* Menu Editor Section */}
        <div className="pt-2">
          <div
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
              isMenuEditorActive ? "text-gray-900" : "text-gray-600",
            )}
          >
            <FileBarChart className="h-5 w-5" />
            Menu Editor
            <ChevronDown
              className={cn(
                "ml-auto h-4 w-4 transition-transform",
                isMenuEditorActive && "rotate-180",
              )}
            />
          </div>
          <div className="ml-4 mt-1 space-y-1">
            {menuEditorItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/menu-editor"}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:block lg:w-64 lg:border-r lg:bg-white">
        <SidebarNav />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarNav onNavigate={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="min-h-screen lg:ml-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-white px-4 lg:px-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {user && <span className="text-sm text-gray-600">{user.name}</span>}
            <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)}>
              <KeyRound className="mr-2 h-4 w-4" />
              Change password
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
